import StarBorderIcon from '@mui/icons-material/StarBorder'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Rating from '@mui/material/Rating'
import Snackbar from '@mui/material/Snackbar'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useMutation } from '@tanstack/react-query'
import KNDialog from 'components/KNDialog/KNDialog'
import KNDialogFormErrors from 'components/KNDialog/KNDialogFormErrors'
import KNFormText from 'components/KNForm/KNFormText'
import { CompanyType } from 'context/authentication/Company.types'
import { UserContext } from 'context/authentication/UserContext'
import { analyticsEvent } from 'global/helpers/analytics'
import { processDefaultValues, processServerErrorMessages } from 'global/helpers/form'
import i18n from 'i18n'
import { type ReactNode, useContext, useEffect, useState } from 'react'
import { Controller, type SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { supportRequest } from './Support.service'
import { type SupportRequestDialogProps, type SupportRequestFormValues } from './SupportRequestDialog'
import { regexEmail } from 'global/helpers/validators.ts'

const StarRatingDialog = ({
  description,
  payload,
  open,
  onAction,
  onClose,
}: SupportRequestDialogProps): ReactNode => {
  const { t } = useTranslation()
  const { handleSubmit, reset, control, formState, setError } = useForm<SupportRequestFormValues>()
  const [showConfirmationSnackbar, setShowConfirmationSnackbar] = useState(false)
  const { user } = useContext(UserContext)
  const [starValue, setStarValue] = useState<number>(payload.starRating ?? 0)
  const [starText, setStarText] = useState<string>()
  const supportRequestMutation = useMutation({
    mutationFn: (data: SupportRequestFormValues) => supportRequest(data, payload.weblinkToken),
    onError: (error) => setError('root', processServerErrorMessages(error)),
  })

  useEffect(() => {
    if (open) {
      reset(
        processDefaultValues({
          language: i18n.language.slice(0, 2),
          url: window.location.href,
          requestType: 'UserSatisfaction',
          customerType: CompanyType.Carrier,
          email: user?.email ?? null,
          title: null,
          message: null,
          starRating: starValue ?? null,
        }),
      )
    }
  }, [open])

  const onSubmit: SubmitHandler<SupportRequestFormValues> = async (data: SupportRequestFormValues) => {
    await supportRequestMutation.mutateAsync(data)
    onAction()
    setShowConfirmationSnackbar(true)
    analyticsEvent('polestar_cs_support_star_rating_sent', [data.starRating.toString()])
  }

  useEffect(() => {
    setStarValue(payload.starRating ?? 0)
  }, [payload.starRating])

  useEffect(() => {
    if (starValue != null) {
      const getText = (value: number) => {
        if (value <= 2) {
          return 'one'
        }
        if (value === 3) {
          return 'three'
        }
        return 'four'
      }
      setStarText(getText(starValue))
    }
  }, [starValue])

  const handleStarTextChange = (newValue: number) => setStarValue(newValue)
  const handleSnackbarClose = (): void => setShowConfirmationSnackbar(false)

  return (
    <>
      <KNDialog
        open={open}
        onClose={onClose}
        maxWidth="xs"
        closeLabel={t('general.cancel')}
        preventClosing={formState.isSubmitting}
        title={t('shared.support.star_rating.give_feedback')}
        actions={
          <Button
            color="secondary"
            variant="contained"
            loading={formState.isSubmitting}
            onClick={handleSubmit(onSubmit)}
          >
            {t('form.send')}
          </Button>
        }
        onSubmit={handleSubmit(onSubmit)}
      >
        <KNDialogFormErrors errors={formState.errors?.root} />
        <Stack spacing={1} mb={2} alignItems="center">
          <Typography>{description ?? t('shared.support.star_rating.scale_notice')}</Typography>
          <Controller
            name="starRating"
            control={control}
            render={({ field }) => (
              <Rating
                name="starRating"
                defaultValue={payload.starRating}
                value={field.value}
                size="large"
                emptyIcon={<StarBorderIcon fontSize="inherit" color="warning" />}
                onChange={(_, newValue) => {
                  field.onChange(newValue)
                  handleStarTextChange(newValue!)
                }}
              />
            )}
          />
          <Typography>
            {starText ? t(`shared.support.star_rating.${starText}`) : ''}
          </Typography>
        </Stack>
        <Stack spacing={2}>
          {payload?.weblinkToken && (
            <KNFormText
              name="email"
              placeholder={t('shared.support.email')}
              control={control}
              rules={{
                pattern: {
                  value: regexEmail,
                  message: t('form.validation.invalid_email'),
                },
                required: t('form.validation.required'),
              }}
            />
          )}
          <KNFormText
            name="message"
            placeholder={t('shared.support.comment')}
            control={control}
            rows={4}
          />
        </Stack>
      </KNDialog>

      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={showConfirmationSnackbar}
        onClose={handleSnackbarClose}
        key="supportRequestConfirmation"
      >
        <Alert variant="filled" severity="success">
          {t('shared.support.star_rating.confirmation')}
        </Alert>
      </Snackbar>
    </>
  )
}

export default StarRatingDialog
